import React, { FC, JSX, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as ReactMapGL from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";
import { FilterContext, FilterContextProvider } from "./contexts/FilterContext";
import { MappingContext } from "@app/mapping/MappingContext";
import { CountyProjectContext } from "../../CountyProjectContext";
import FilterControl from "./controls/FilterControl";
import { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import NetworkCoverageMapLayerControl from "./controls/NetworkCoverageMapLayerControl";
import NetworkCoverageMapLegendControl from "./controls/NetworkCoverageMapLegendControl";
import { QESAlert } from "@app/shared/qes-standards";
import "mapbox-gl/dist/mapbox-gl.css";
import { ProjectionProviderType, ProviderNetworkType } from '@app/projects/enumerators';
import { ProviderAccordionControl } from './controls/ProviderAccordionControl';
import { NetworkCoverageMapDataContextProvider, NetworkCoverageMapDataContext } from './contexts/NetworkCoverageMapDataContext';
import MapDetailsBox from './MapDetailsBox';
import NetworkCoverageMapProviderPopup from './NetworkCoverageMapProviderPopup';
import FlyoutContextProvider from '@app/shared/Flyouts/FlyoutContext';
import {
  DEFAULT_COLOR,
  MARKET_PROVIDER_COLOR,
  MARKET_PROVIDER_ICON_SHADOW_COLOR,
  MEMBERS_WITH_ACCESS_COLOR,
  MEMBERS_WITHOUT_ACCESS_COLOR,
  NETWORK_PROVIDER_COLOR,
  NETWORK_PROVIDER_ICON_SHADOW_COLOR,
  PROSPECT_PROVIDER_COLOR,
  PROSPECT_PROVIDER_ICON_SHADOW_COLOR,
  TARGET_PROVIDER_COLOR,
  TARGET_PROVIDER_ICON_SHADOW_COLOR
} from './NetworkCoverageMapsColors';
import { ProviderSourceOfSelection } from './enumerators';
import { ImpactProviderMapResults, MemberMapResults } from '@app/projects/results/common/mapping';
import MapExport from './MapExport';
import mapboxgl from 'mapbox-gl';
import { RecenterControl } from './controls/RecenterControl';

const MEMBERS_IMAGE_ID = "members-image";
const NETWORK_PROVIDER_IMAGE_ID = "network-provider-image";
const MARKET_PROVIDER_IMAGE_ID = "market-provider-image";
const PROSPECT_PROVIDER_IMAGE_ID = "prospect-provider-image";
const TARGET_PROVIDER_IMAGE_ID = "target-provider-image";

const countyBorderSourceId = "county-borders-source";
const countyBorderLayerId = "county-borders-layer";

const memberSourceId = "members-source"
const membersLayerId = "members-layer";

const networkProviderSourceId = "network-providers";
const networkProviderLayerId = "network-providers-layer";
const networkProviderHighlightedLayerId = "network-providers-layer-highlighted";

const impactProviderSourceId = "impact-providers";
const impactProviderLayerId = "impact-providers-layer";
const impactProviderHighlightedLayerId = "impact-providers-layer-highlighted";

const interactiveLayerIds = [networkProviderLayerId, impactProviderLayerId];

type NetworkCoverageMapProps = {
  processProviderChanges: (reprocessFailure: boolean) => Promise<void>;
  setProjectHasUnprocessedProjectionProviders: (has: boolean) => void;
}

const WrappedNetworkCoverageMap: FC<React.PropsWithChildren<NetworkCoverageMapProps>> = (props: NetworkCoverageMapProps): JSX.Element => {
  // Check for browser support
  if (!mapboxgl.supported()) {
    return (
      <div>
        <QESAlert type="warning" persist={true} >
          <strong>Your browser does not support our mapping application. QES Maps support the latest version of Chrome, Edge, Firefox, and Safari.</strong>
        </QESAlert>
      </div>
    );
  }

  const history = useHistory();

  // Contexts
  const filterContext = useContext(FilterContext);
  const mappingContext = useContext(MappingContext);
  const projectContext = useContext(CountyProjectContext);
  const networkCoverageMapDataContext = useContext(NetworkCoverageMapDataContext);

  // Refs
  const mapRef = useRef<MapRef>(null);

  // Map Defaults
  const defaultBearing = mappingContext.defaultBearing;
  const defaultBounds: ReactMapGL.LngLatBoundsLike = mappingContext.defaultBoundaries;
  const defaultDragRotate = mappingContext.defaultDragRotate;
  const defaultLatitude = mappingContext.defaultLatitude;
  const defaultLongitude = mappingContext.defaultLongitude;
  const defaultPadding: ReactMapGL.PaddingOptions = mappingContext.defaultPadding;
  const defaultPitch = mappingContext.defaultPitch;
  const defaultTouchPitch = mappingContext.defaultTouchPitch;
  const defaultZoom = mappingContext.defaultZoom;

  // Images constants
  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const images = [
    { id: MEMBERS_IMAGE_ID, path: `${imagesDir}Members.png` },
    { id: NETWORK_PROVIDER_IMAGE_ID, path: `${imagesDir}NetworkProvider.png` },
    { id: MARKET_PROVIDER_IMAGE_ID, path: `${imagesDir}MarketProvider.png` },
    { id: PROSPECT_PROVIDER_IMAGE_ID, path: `${imagesDir}ProspectProvider.png` },
    { id: TARGET_PROVIDER_IMAGE_ID, path: `${imagesDir}TargetedProvider.png` }
  ];

  const [viewState, setViewState] = useState<ReactMapGL.ViewState>({
    bearing: defaultBearing,
    latitude: defaultLatitude,
    longitude: defaultLongitude,
    padding: defaultPadding,
    pitch: defaultPitch,
    zoom: defaultZoom,
  });

  const clearAllFilters = () => {
    projectContext.filters.forEach((filter) => {
      if (filter.urlPart) {
        history.location.pathname = history.location.pathname.replace(filter.urlPart, "");
      }
    });
    history.push(history.location.pathname);
    projectContext.clearFilters();
    filterContext.setSelectedProviderSources([ProviderNetworkType.Network]);
  };

  const updateMapInputs = () => {
    if (filterContext.isFilterActive) {
      filterContext.setIsFilterActive(false);
    }
    networkCoverageMapDataContext.resetMapData();
  }

  const recenterMap = useCallback((): void => {
    setViewState({
      ...viewState,
      bearing: defaultBearing,
      latitude: defaultLatitude,
      longitude: defaultLongitude,
      padding: defaultPadding,
      pitch: defaultPitch,
      zoom: defaultZoom,
    });
  }, [viewState]);

  const createMap = useCallback(async (): Promise<void> => {
    const map = mapRef.current;
    if (!map) return;
    filterContext.setIsFilterActive(true);
  }, [
    filterContext.selectedServiceArea,
    filterContext.selectedSpecialty,
    networkCoverageMapDataContext.memberData,
    networkCoverageMapDataContext.networkProviders,
    networkCoverageMapDataContext.impactProviders,
  ]);

  const onMapLoad = useCallback((): void => {

    const map = mapRef.current?.getMap();
  
    if (!map) return;

    const handleMapStyleChange = () => {
      //When changing the styles, even though the Source and Layers are preserved, we need to re-add the images, since they are tied to the style object
      loadImagesToMap(map);
    }
    map.on('styledata', handleMapStyleChange);
    
    loadImagesToMap(map);//Initial load of images

    mapRef.current.setCenter([defaultLongitude, defaultLatitude]);
    
  }, [defaultLongitude, defaultLatitude]);

  const onMapMove = useCallback((viewState: ReactMapGL.ViewState): void => {
    setViewState({
      ...viewState,
      // Disables the map rotation changes (keyboard, mouse, touch)
      bearing: defaultBearing,
      // Disables the map pitch changes (keyboard, mouse, touch)
      pitch: defaultPitch
    });
  }, []);

  const loadImagesToMap = (map: ReactMapGL.MapInstance): void => {
    images.forEach(({ id, path }) => {
      if (!map.hasImage(id)) {
        map.loadImage(path, (error: Error | null, image: HTMLImageElement | undefined) => {
          if (error || !image) return;
          if (!map.hasImage(id)) {
              map.addImage(id, image, { sdf: true });
          }
        });
      }
    });
  }

  const handleProviderPopupClose = () => {
    networkCoverageMapDataContext.setIsProviderPopupOpen(false);
    networkCoverageMapDataContext.setSelectedProvider(null);
    networkCoverageMapDataContext.setSelectedProviderSourceOfSelection(ProviderSourceOfSelection.Unknown);
  };

  const handleMapClick = (e: ReactMapGL.MapMouseEvent) => {
    const providers = e.features;

    if (!providers || providers.length === 0) {
      return;
    }

    const clickedProvider = providers.find((p) =>
      interactiveLayerIds.includes(p.layer.id)
    );

    if (clickedProvider && clickedProvider.properties) {
      const providerId = clickedProvider.properties.providerId;
      const selectedProvider = networkCoverageMapDataContext.networkProviders?.find(item => item.providerId === providerId) ||
        networkCoverageMapDataContext.impactProviders?.find(item => item.providerId === providerId);

      if (!!selectedProvider) {
        networkCoverageMapDataContext.setSelectedProvider(selectedProvider);
        networkCoverageMapDataContext.setSelectedProviderSourceOfSelection(ProviderSourceOfSelection.Map);
        networkCoverageMapDataContext.setIsProviderPopupOpen(true);
      }
    }
  };

  const handleMapMove = useCallback(() => {
    const newBounds = mapRef.current.getBounds();
    networkCoverageMapDataContext.setCurrentMapBoundary(newBounds);
  }, []);

  const handleRecenterIconClick = () => {
    if (networkCoverageMapDataContext.dataLoaded && !!networkCoverageMapDataContext.dataBoundingBox) {
        mapRef.current.fitBounds(networkCoverageMapDataContext.dataBoundingBox, { duration: 1000, padding: 50 });
    } else {
      recenterMap();  
    }
  }

  const triggerDataRefresh = (): void => {
    handleProviderPopupClose();
    networkCoverageMapDataContext.setImpactProviders([]);
    networkCoverageMapDataContext.setDataRefreshCounter(prev => prev + 1);  
  };

  const handleProcessTargetProviderChanges = async (reprocessFailure: boolean): Promise<void> => {
    props.processProviderChanges(reprocessFailure);
    triggerDataRefresh();
  };

  const handlePojectHasUnprocessedProjectionProviders = (has: boolean): void => {
    props.setProjectHasUnprocessedProjectionProviders(has);
    triggerDataRefresh();
  };

  useEffect(() => {
    handleRecenterIconClick();
  }, [networkCoverageMapDataContext.dataBoundingBox, networkCoverageMapDataContext.dataLoaded]);

  useEffect(() => {
    if (!!networkCoverageMapDataContext.selectedProvider) {
      const selectedProviderCard = networkCoverageMapDataContext.selectedProvider;

      setViewState((prev) => ({
        ...prev,
        latitude: selectedProviderCard.latitude,
        longitude: selectedProviderCard.longitude,
        transitionDuration: 1000
      }));
    }
  }, [networkCoverageMapDataContext.selectedProvider]);

  return (
    <div>
      {networkCoverageMapDataContext.currentAdequacySummarySpecialtyGroup &&
        <MapExport
          isLoading={networkCoverageMapDataContext.loadingProviders}
          specialty={networkCoverageMapDataContext.currentAdequacySummarySpecialtyGroup.footnote.specialtyGroupName}
          serviceArea={networkCoverageMapDataContext.currentAdequacySummarySpecialtyGroup.footnote.geographyName}
          runConfigurationDateCreated={projectContext.runConfiguration.dateCreated}
          projectName={projectContext.project.resource.name}
        />
      }

      <div className="network-coverage-map__map-container">
        {networkCoverageMapDataContext.currentAdequacySummarySpecialtyGroup &&
          <MapDetailsBox
            adequacySummarySpecialtyGroup={networkCoverageMapDataContext.currentAdequacySummarySpecialtyGroup}
            cmsCreditIsApplying={projectContext.cmsCreditIsApplying}
            project={projectContext.project}
            runConfiguration={projectContext.runConfiguration}
          />
        }

        <div className="network-coverage-map__map">
          <ReactMapGL.Map
            {...viewState}
            boxZoom={false}
            dragRotate={defaultDragRotate}
            mapboxAccessToken={mappingContext.mapboxApiKey}
            padding={defaultPadding}
            mapStyle={networkCoverageMapDataContext.mapStyle}
            maxBounds={defaultBounds}
            onClick={handleMapClick}
            interactiveLayerIds={interactiveLayerIds}
            onLoad={onMapLoad}
            onMove={(e) => onMapMove(e.viewState)}
            onMoveEnd={handleMapMove}
            preserveDrawingBuffer={true}
            projection="mercator"
            ref={mapRef}
            reuseMaps={false}
            style={{ fontFamily: "Avenir Next LT Pro, Calibri, sans-serif" }} // Override ReactMapGL default font-family
            touchPitch={defaultTouchPitch}
            logoPosition='bottom-right'
            cooperativeGestures={true}
          >
            {/* The Mapbox Expression logic docs: https://docs.mapbox.com/style-spec/reference/expressions/ */}
            {/* County Borders Layer */}
            <Source id={countyBorderSourceId} type="geojson" data={networkCoverageMapDataContext.countyBorderData ?? { features: [], type: "FeatureCollection" }}>
              <Layer
                id={countyBorderLayerId}
                type="line"
                paint={{
                  "line-color": MARKET_PROVIDER_COLOR,
                  "line-width": 1.5,
                }}
              />
            </Source>

            {/* Members Layer */}
            <Source
              id={memberSourceId}
              type="geojson"
              data={networkCoverageMapDataContext.memberData ?? { features: [], type: "FeatureCollection" }}
            >
              <Layer
                id={membersLayerId}
                type="symbol"
                layout={{
                  "icon-allow-overlap": true,
                  "icon-image": MEMBERS_IMAGE_ID,
                  "icon-size": 0.2
                }}
                paint={{
                  'icon-color': [
                    'case',
                    ['==', ['get', nameof<MemberMapResults>(x => x.covered)], true], // Get the 'covered' property from the member data where it's true
                    MEMBERS_WITH_ACCESS_COLOR, // If the member has access
                    MEMBERS_WITHOUT_ACCESS_COLOR // If member does not have access
                  ]
                }}
                filter={[
                  'case',
                    ['==', ['get', nameof<MemberMapResults>(o => o.covered)], true],//case the member is covered
                      networkCoverageMapDataContext.showMembersWithAccess,// Show members with access if selected
                    ['==', ['get', nameof<MemberMapResults>(o => o.covered)], false],//case the member is not covered
                      networkCoverageMapDataContext.showMembersWithoutAccess,// Show members without access if selected
                    // Default case
                      false // If neither are selected, don't show any members
                ]}
              />
            </Source>

            {/* Network Providers Layer */}
            <Source
              id={networkProviderSourceId}
              type="geojson"
              data={{
                features: networkCoverageMapDataContext.visibleNetworkProviders
                  ? networkCoverageMapDataContext.visibleNetworkProviders.map((provider) => ({
                    geometry: {
                      coordinates: [provider.longitude, provider.latitude],
                      type: "Point",
                    },
                    properties: provider,
                    type: "Feature",
                  }))
                  : [],
                type: "FeatureCollection",
              }}
            >
              {/* Layer to represent the shadow sorrounding the Network provider icons once selected */}
              <Layer
                id={networkProviderHighlightedLayerId}
                type="circle"
                paint={{
                  'circle-blur': 1,
                  'circle-color': NETWORK_PROVIDER_ICON_SHADOW_COLOR,
                  'circle-radius': [
                    'case',
                    // Check if the provider is selected
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerId)], networkCoverageMapDataContext.selectedProvider?.providerId ?? null],
                    40, // radius when selected
                    0, // radius when not selected
                  ]
                }}
              />

              <Layer
                id={networkProviderLayerId}
                type="symbol"
                layout={{
                  "icon-allow-overlap": true,
                  'icon-image': NETWORK_PROVIDER_IMAGE_ID,
                  'icon-size': [
                    'case',
                    // Check if the provider is selected
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerId)], networkCoverageMapDataContext.selectedProvider?.providerId ?? null],
                    0.4, // size when selected
                    0.2 // size when not selected
                  ]
                }}
                paint={{
                  'icon-color': NETWORK_PROVIDER_COLOR
                }}
              />
            </Source>

            <Source
              id={impactProviderSourceId}
              type="geojson"
              data={{
                features: networkCoverageMapDataContext.visibleImpactProviders
                  ? networkCoverageMapDataContext.visibleImpactProviders.map((provider) => ({
                    geometry: {
                      coordinates: [provider.longitude, provider.latitude],
                      type: "Point",
                    },
                    properties: provider,
                    type: "Feature",
                  }))
                  : [],
                type: "FeatureCollection",
              }}
            >
              <Layer
                id={impactProviderHighlightedLayerId}
                type="circle"
                paint={{
                  'circle-blur': 1,
                  'circle-color': [
                    'case',
                    // Check if the provider is selected
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerId)], networkCoverageMapDataContext.selectedProvider?.providerId ?? null],
                    [
                      'case',
                      // If the provider is a target provider, use the target provider color
                      ['==', ['get', nameof<ImpactProviderMapResults>(x => x.projectionType)], ProjectionProviderType.Target],
                      TARGET_PROVIDER_ICON_SHADOW_COLOR,
                      // If the provider is a market provider, use the market provider color
                      ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Market],
                      MARKET_PROVIDER_ICON_SHADOW_COLOR,
                      // If the provider is a prospect provider, use the prospect provider color
                      ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Prospect],
                      PROSPECT_PROVIDER_ICON_SHADOW_COLOR,
                      // Default
                      DEFAULT_COLOR
                    ],
                    // Default
                    DEFAULT_COLOR
                  ],
                  'circle-radius': [
                    'case',
                    // Check if the provider is selected
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerId)], networkCoverageMapDataContext.selectedProvider?.providerId ?? null],
                    40, // radius when selected
                    0, // radius when not selected
                  ]
                }}
              />

              <Layer
                id={impactProviderLayerId}
                type="symbol"
                filter={[
                  'any',
                  // Filter to show the market providers
                  ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Market],
                  // Filter to show the prospect providers
                  ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Prospect],
                  // Since target providers are not a part of network type and are a projection type, they fall under one of the above two filters
                ]}
                layout={{
                  "icon-allow-overlap": true,
                  "icon-image": [
                    // Use a case statement to determine the icon based on the provider type
                    'case',
                    // If the provider is a target provider, use the target provider icon. Target goes first to ensure it takes precedence over the others
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.projectionType)], ProjectionProviderType.Target],
                    TARGET_PROVIDER_IMAGE_ID,
                    // If the provider is a market provider, use the market provider icon
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Market],
                    MARKET_PROVIDER_IMAGE_ID,
                    // If the provider is a prospect provider, use the prospect provider icon
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Prospect],
                    PROSPECT_PROVIDER_IMAGE_ID,
                    // Default
                    NETWORK_PROVIDER_IMAGE_ID
                  ],
                  'icon-size': [
                    'case',
                    // Check if the provider is selected
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerId)], networkCoverageMapDataContext.selectedProvider?.providerId ?? null],
                    0.4, // size when selected
                    0.2 // size when not selected
                  ]
                }}
                paint={{
                  'icon-color': [
                    // Use a case statement to determine the color based on the provider type
                    'case',
                    // If the provider is a target provider, use the target provider color. Target goes first to ensure it takes precedence over the others
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.projectionType)], ProjectionProviderType.Target],
                    TARGET_PROVIDER_COLOR,
                    // If the provider is a market provider, use the market provider color
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Market],
                    MARKET_PROVIDER_COLOR,
                    // If the provider is a prospect provider, use the prospect provider color
                    ['==', ['get', nameof<ImpactProviderMapResults>(x => x.providerNetworkType)], ProviderNetworkType.Prospect],
                    PROSPECT_PROVIDER_COLOR,
                    // Default
                    DEFAULT_COLOR
                  ]
                }}
              />
            </Source>

            {/* Popup displayed above the highlighted icon */}
            {!!networkCoverageMapDataContext.selectedProvider &&
              networkCoverageMapDataContext.isProviderPopupOpen &&
              <NetworkCoverageMapProviderPopup
                provider={networkCoverageMapDataContext.selectedProvider}
                processProviderChanges={handleProcessTargetProviderChanges}
                setProjectHasUnprocessedProjectionProviders={handlePojectHasUnprocessedProjectionProviders}
                onClose={handleProviderPopupClose}
              />
            }

            {/* Zoom Control */}
            <NavigationControl data-html2canvas-ignore="true" position="top-right" showCompass={false} />

            <RecenterControl ignoreInMapExport={true} imagePath={`${imagesDir}Recenter.png`} mapRef={mapRef} onClick={handleRecenterIconClick} position="top-right"/>

            {filterContext.isFilterActive &&
              <NetworkCoverageMapLayerControl ignoreInMapExport={true} imagePath={`${imagesDir}Layer_Hover.png`} mapRef={mapRef} position="top-right" />
            }

            {filterContext.isFilterActive &&
              <NetworkCoverageMapLegendControl mapRef={mapRef} position="bottom-right" />
            }

            {!filterContext.isFilterActive &&
              <FilterControl ignoreInMapExport={true} clearAllFilters={clearAllFilters} createMap={createMap} mapRef={mapRef} position="top-left" />
            }

            {filterContext.isFilterActive &&
              <ProviderAccordionControl ignoreInMapExport={true} updateMapInputs={updateMapInputs} mapRef={mapRef} position="top-left" />
            }

          </ReactMapGL.Map>
        </div>
      </div>
    </div>
  );
};

export const NetworkCoverageMap: FC<React.PropsWithChildren<NetworkCoverageMapProps>> = (props: NetworkCoverageMapProps) => (
  <FilterContextProvider>
    <NetworkCoverageMapDataContextProvider>
      <FlyoutContextProvider>
        <WrappedNetworkCoverageMap {...props} />
      </FlyoutContextProvider>
    </NetworkCoverageMapDataContextProvider>
  </FilterContextProvider>
);